import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

/**
 * MessageComposer - Professional message input component with succession theming
 * 
 * Features rich text input, send button, keyboard shortcuts, and connection status with marketplace colors
 */
const MessageComposer = ({ 
  currentUser,
  conversationId,
  onSendMessage,
  onTyping,
  disabled = false,
  theme = 'light'
}) => {
  const intl = useIntl();
  const [value, setValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const fileObjectsRef = useRef({});

  const handleSendClick = async () => {
    if (!canSend) return;
    
    setIsSending(true);
    
    try {
      const messageData = {
        body: value,
        attachments: attachedFiles.map(fileMetadata => {
          const actualFile = fileObjectsRef.current[fileMetadata.id];
          return {
            name: fileMetadata.name,
            size: fileMetadata.size,
            type: fileMetadata.type,
            file: actualFile
          };
        })
      };
      
      await onSendMessage(messageData);
      
      // Clear input after successful send
      setValue('');
      
      // Clean up after sending
      const filesToCleanup = [...attachedFiles];
      setAttachedFiles([]);
      
      // Clean up object URLs and file refs
      filesToCleanup.forEach(file => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
        delete fileObjectsRef.current[file.id];
      });
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const canSend = !disabled && !isSending && (value?.trim() || attachedFiles.length > 0);

  // Handle typing indicator
  const handleTypingStart = () => {
    if (onTyping) {
      onTyping(true);
      
      // Clear existing timeout
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
      
      // Set new timeout to stop typing
      const timeout = setTimeout(() => {
        onTyping(false);
      }, 3000);
      
      setTypingTimeout(timeout);
    }
  };

  const handleTypingStop = () => {
    if (onTyping) {
      onTyping(false);
    }
    if (typingTimeout) {
      clearTimeout(typingTimeout);
      setTypingTimeout(null);
    }
  };

  const handleInputChange = (e) => {
    setValue(e.target.value);
    handleTypingStart();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendClick();
    }
  };

  // File handling functions
  const handleFileSelect = (files) => {
    const newFiles = Array.from(files).map(file => {
      const fileId = Date.now() + Math.random();
      const fileMetadata = {
        id: fileId,
        name: file.name,
        size: file.size,
        type: file.type,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
      };
      
      // Store actual file object
      fileObjectsRef.current[fileId] = file;
      
      return fileMetadata;
    });
    
    setAttachedFiles(prev => [...prev, ...newFiles]);
  };

  const handleFileRemove = (fileId) => {
    setAttachedFiles(prev => {
      const fileToRemove = prev.find(f => f.id === fileId);
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      delete fileObjectsRef.current[fileId];
      return prev.filter(f => f.id !== fileId);
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return '🖼️';
    if (type.startsWith('video/')) return '🎥';
    if (type.startsWith('audio/')) return '🎵';
    if (type.includes('pdf')) return '📄';
    if (type.includes('word')) return '📝';
    if (type.includes('excel') || type.includes('spreadsheet')) return '📊';
    return '📁';
  };

  return (
    <div className="relative p-4 bg-[var(--succession-bg-primary)]">
      {/* File Previews - Demo style */}
      {attachedFiles.length > 0 && (
        <div className="mb-3 p-3 bg-[var(--succession-bg-secondary)] rounded-lg border border-[var(--succession-border)]">
          <div className="text-xs font-medium text-[var(--succession-text-secondary)] mb-2">
            {intl.formatMessage({ id: 'succession.messaging.message_composer.files_attached' }, { count: attachedFiles.length, plural: attachedFiles.length > 1 ? 's' : '' })}
          </div>
          <div className="flex flex-col gap-2">
            {attachedFiles.map(file => (
              <div key={file.id} className="flex items-center gap-3 p-2 bg-[var(--succession-bg-primary)] rounded-lg">
                {file.preview ? (
                  <img src={file.preview} alt={file.name} className="w-10 h-10 object-cover rounded" />
                ) : (
                  <div className="w-10 h-10 flex items-center justify-center bg-[var(--succession-bg-secondary)] rounded text-lg">
                    {getFileIcon(file.type)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[var(--succession-text-primary)] truncate">{file.name}</div>
                  <div className="text-xs text-[var(--succession-text-muted)]">{formatFileSize(file.size)}</div>
                </div>
                <button
                  onClick={() => handleFileRemove(file.id)}
                  className="p-1.5 rounded hover:bg-red-50 text-[var(--succession-text-muted)] hover:text-red-500 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Drag & Drop Overlay */}
      {dragOver && (
        <div className="absolute inset-0 bg-[#4A90E2]/10 border-2 border-dashed border-[#4A90E2] rounded-lg flex items-center justify-center z-10">
          <div className="text-center">
            <div className="text-3xl mb-2">📁</div>
            <div className="text-[#4A90E2] font-medium">{intl.formatMessage({ id: 'succession.messaging.message_composer.drop_files_here' })}</div>
          </div>
        </div>
      )}

      {/* Main Input Area - Demo style */}
      <div className="flex items-end gap-3">
        {/* Message Input - Demo style: simple, clean */}
        <div className="flex-1">
          <input
            type="text"
            value={value}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setIsFocused(false);
              handleTypingStop();
            }}
            placeholder={intl.formatMessage({ id: 'succession.messaging.message_composer.placeholder' })}
            disabled={disabled}
            className={`w-full py-3 px-4 bg-[var(--succession-bg-secondary)] border border-[var(--succession-border)] rounded-lg text-sm text-[var(--succession-text-primary)] placeholder-[var(--succession-text-muted)] outline-none focus:border-[#4A90E2] focus:ring-2 focus:ring-[#4A90E2]/20 transition-all ${
              disabled ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          />
        </div>

        {/* Send Button - primary color when active, gray when disabled */}
        <button
          onClick={handleSendClick}
          disabled={!canSend}
          className="px-5 py-3 rounded-lg font-medium text-sm transition-colors"
          style={{
            backgroundColor: canSend ? 'var(--succession-primary)' : 'var(--succession-bg-tertiary)',
            color: canSend ? '#ffffff' : 'var(--succession-text-muted)',
            cursor: canSend ? 'pointer' : 'not-allowed',
            opacity: canSend ? 1 : 0.6
          }}
        >
          {isSending ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            intl.formatMessage({ id: 'succession.messaging.message_composer.send_button' })
          )}
        </button>
      </div>
    </div>
  );
};

MessageComposer.propTypes = {
  currentUser: PropTypes.object,
  conversationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSendMessage: PropTypes.func.isRequired,
  onTyping: PropTypes.func,
  disabled: PropTypes.bool,
  theme: PropTypes.string
};

export default MessageComposer; 